import { useCallback, useState } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";
import { twMerge } from "tailwind-merge";

import { gql } from "@apollo/client";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@components/v2/buttons";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { useHiddenBudgetMixes } from "@features/mmm/hooks/use-hidden-budget-mixes";
import {
  useFeatureFlags,
  useNorthbeamMutation,
  useNorthbeamMutationPromise,
} from "@hooks/http";
import {
  type DestroyMMMClientSetting,
  type DestroyMMMClientSettingVariables,
  type TriggerOptimizerRunForClientSettings,
  type TriggerOptimizerRunForClientSettingsVariables,
  CreateBudgetRecommendationRunResultStatusGQLEnum,
} from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";
import { formatDecimal } from "@north-beam/nb-common/formatting";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { BUDGET_MIXES_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { Modal } from "@shared/modal";
import Spinner from "@shared/spinner";
import { Switch } from "@shared/switch";

import { useMMMAdminMode } from "../budget-mix-table/utils";

import { ClientSettingNetworkErrorModal } from "./client-setting-network-error-modal";
import { DESTROY_CLIENT_SETTING } from "./form/destroy-query";
import { getClientSettingLatestBudgetGenerationResult } from "./helpers/get-client-setting-latest-budget-generation-result";
import { getIsClientSettingGeneratingBudgets } from "./helpers/get-is-client-setting-generating-budgets";
import { useUpsertClientSetting } from "./hooks/use-upsert-client-setting";
import { SimpleSpendConstraintsTable } from "./simple-spend-constraints-table";
import { KpiType, type ClientSetting } from "./types";

export function ClientSettingCard({
  clientSetting,
  color,
  onEditClientSetting,
  canAlter,
  canEdit,
}: {
  clientSetting: ClientSetting;
  color: string;
  onEditClientSetting: (clientSettingToEdit: ClientSetting) => void;
  canAlter: boolean;
  canEdit: boolean;
}) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const { selectedCycle } = useSelectedCycle();
  const { hasFeatureFlag } = useFeatureFlags();

  const [destroyErrorMessage, setDestroyErrorMessage] = useState<null | string>(
    null,
  );
  const [showDuplicateSettingErrorModal, setShowDuplicateSettingErrorModal] =
    useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { setClientSettings } = useClientSettings();
  const { kpi: clientSettingKpi, spendConstraints } =
    useClientSettingData(clientSetting);
  const [, , { setHiddenClientSettings, hiddenClientSettings }] =
    useHiddenBudgetMixes();
  const [budgetMixes, setBudgetMixes] = useBudgetMixes();
  const clientSettingIsGeneratingBudgets =
    getIsClientSettingGeneratingBudgets(clientSetting);

  const hadGeneratingBudgetsError =
    getClientSettingLatestBudgetGenerationResult(clientSetting) ===
    CreateBudgetRecommendationRunResultStatusGQLEnum.failed;

  // TODO: After client settings are pulled directly from DM, add a refetch
  // to this mutation to compliment the optimistic rendering
  const [destroyClientSetting, { loading: loadingDestroyClientSetting }] =
    useNorthbeamMutationPromise<
      DestroyMMMClientSetting,
      DestroyMMMClientSettingVariables
    >(DESTROY_CLIENT_SETTING, {
      doNotRaiseErrorOnFailure: true,
      refetchQueries: [
        {
          query: BUDGET_MIXES_FOR_CYCLE_QUERY,
          variables: { id: selectedCycle.id },
        },
      ],
      awaitRefetchQueries: true,
    });

  const {
    upsertClientSetting,
    loading: loadingUpsertClientSetting,
    transformClientSettingToPayload,
  } = useUpsertClientSetting();

  const formatCurrency = (amount: number) =>
    formatting
      .formatCurrency(amount, {
        precision: "exact",
        code: user.currencies.currentCurrency,
      })
      .replace(/\..*/, "");

  const handleConfirmDelete = async () => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.DELETE_CLIENT_SETTING_BUTTON,
      action: "onClick",
    });

    try {
      const { destroyMMMClientSetting: res } = await destroyClientSetting({
        id: clientSetting.id,
      });
      if (destroyErrorMessage) {
        setDestroyErrorMessage(null);
        return;
      }

      setClientSettings((previousSettings) =>
        previousSettings.filter((setting) => setting.id !== res.id),
      );
      setBudgetMixes((prevMixes) =>
        prevMixes.filter((mix) => mix.client_setting_id !== res.id),
      );
      setIsConfirmingDelete(false);
    } catch (err) {
      setDestroyErrorMessage("There was a problem deleting your setting");
    }
  };

  const handleCancelConfirmDelete = () => {
    setDestroyErrorMessage(null);
    setIsConfirmingDelete(false);
  };

  const handleDuplicateSetting = async () => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.DUPLICATE_CLIENT_SETTING_BUTTON,
      action: "onClick",
    });

    const payload = transformClientSettingToPayload(
      clientSetting,
      selectedCycle,
    );
    const wasSuccessful = await upsertClientSetting(payload);
    if (!wasSuccessful) {
      setShowDuplicateSettingErrorModal(true);
    }
  };

  const budgetMixesFromClientSetting = budgetMixes.filter(
    ({ client_setting_id }) => client_setting_id === clientSetting.id,
  );

  return isConfirmingDelete ? (
    <ClientSettingConfirmDeleteCard
      settingName={clientSetting.name}
      onDelete={handleConfirmDelete}
      onCancel={handleCancelConfirmDelete}
      isDestroying={loadingDestroyClientSetting}
      errorText={destroyErrorMessage}
    />
  ) : (
    <div>
      <div className="mb-2 flex border border-v2-secondary rounded-md pl-4 pr-4 pt-2 pb-2 gap-6 items-center relative">
        <div
          className="absolute -left-0.5 top-0 bottom-0 rounded-l-md w-2"
          style={{ backgroundColor: color }}
        ></div>
        <div className="flex justify-between items-center w-full">
          <div className="flex justify-start items-center">
            <Switch
              id={`client-setting-${clientSetting.id}`}
              size="medium"
              className="mr-4"
              disabled={budgetMixesFromClientSetting.length === 0}
              style={{ "--switch-color": color }}
              value={!hiddenClientSettings[clientSetting.id]}
              onChange={(val) => {
                setHiddenClientSettings((curr) => ({
                  ...curr,
                  [clientSetting.id]: !val,
                }));
              }}
            />
            <div className="flex flex-col justify-start items-start">
              <div className="flex gap-2 justify-start items-center">
                <div
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: color }}
                ></div>
                <span className="text-lg font-semibold">
                  {clientSetting.name}
                </span>
              </div>
              <div className="flex justify-start items-center gap-4">
                <div className="flex justify-start items-center gap-2">
                  <span className="text-2xs text-v2-secondary">
                    Target Budget
                  </span>
                  <span className="text-2xs">
                    {formatCurrency(clientSetting.target_spend)}
                  </span>
                </div>
                <div className="flex justify-start items-center gap-2">
                  <span className="text-2xs text-v2-secondary">
                    Budget Range
                  </span>
                  <span className="text-2xs">
                    {formatCurrency(clientSetting.budget_low)} -{" "}
                    {formatCurrency(clientSetting.budget_high)}
                  </span>
                </div>
                <div className="flex justify-start items-center gap-2">
                  <span className="text-2xs text-v2-secondary uppercase">
                    KPI
                  </span>
                  <span className="text-2xs">
                    {clientSettingKpi?.friendly_name}
                  </span>
                </div>
                {hasFeatureFlag("allowDesiredKpiValueForMMM") &&
                  clientSetting.desired_kpi_value && (
                    <div className="flex justify-start items-center gap-2">
                      <span className="text-2xs text-v2-secondary ">
                        KPI Desired Value
                      </span>
                      <span className="text-2xs">
                        {clientSettingKpi?.kpi_type === KpiType.REVENUE
                          ? formatCurrency(clientSetting.desired_kpi_value)
                          : formatDecimal(clientSetting.desired_kpi_value, {
                              precision: "exact",
                            })}
                      </span>
                    </div>
                  )}
                <GenericTooltip
                  noInfoCircle
                  placement="bottom"
                  overlayClassName="m-0 p-3"
                  className="m-0 p-0"
                  content={
                    spendConstraints.length ? (
                      <SimpleSpendConstraintsTable
                        spendConstraints={spendConstraints}
                      />
                    ) : (
                      <div>
                        <h5 className="text-2xs font-bold mb-1.5">
                          No spend requirements
                        </h5>
                        <p className="text-xxs m-0">
                          Settings without requirements create better KPI
                          forecasts
                        </p>
                      </div>
                    )
                  }
                >
                  <div
                    className={clsx(
                      "flex justify-start gap-2 items-center bg-v2-highlight-information rounded-full w-fit py-1 px-2",
                      {
                        "cursor-pointer": spendConstraints.length,
                      },
                    )}
                  >
                    <span className="text-2xs text-v2-action">
                      Spend requirements
                    </span>
                    <span className="text-2xs">{spendConstraints.length}</span>
                  </div>
                </GenericTooltip>
              </div>
            </div>
          </div>
          <ClientSettingsButtonContainer
            shouldDisplay={canAlter}
            isDisabled={clientSetting.locked}
            isEditingDisabled={!canEdit}
            onDelete={() => setIsConfirmingDelete(true)}
            onDuplicate={handleDuplicateSetting}
            onEdit={() => onEditClientSetting(clientSetting)}
            clientSetting={clientSetting}
            isDuplicating={loadingUpsertClientSetting}
            isGeneratingBudgets={clientSettingIsGeneratingBudgets}
            hadGeneratingBudgetsError={hadGeneratingBudgetsError}
          />
        </div>
      </div>
      <ClientSettingNetworkErrorModal
        isOpen={showDuplicateSettingErrorModal}
        closeModal={() => setShowDuplicateSettingErrorModal(false)}
        titleText="There was a problem duplicating your setting"
      />
    </div>
  );
}

function ClientSettingsButtonContainer({
  shouldDisplay,
  onDelete,
  onDuplicate,
  onEdit,
  isDuplicating,
  isDisabled,
  isEditingDisabled,
  clientSetting,
  isGeneratingBudgets,
  hadGeneratingBudgetsError,
}: {
  shouldDisplay: boolean;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  isDuplicating: boolean;
  isDisabled: boolean;
  isEditingDisabled: boolean;
  clientSetting: ClientSetting;
  isGeneratingBudgets: boolean;
  hadGeneratingBudgetsError: boolean;
}) {
  return shouldDisplay ? (
    <div className="flex justify-end gap-4 items-center">
      <ClientSettingDeleteButton
        onClick={onDelete}
        isDisabled={isDisabled || isGeneratingBudgets}
      />
      <ClientSettingDuplicateButton
        onClick={onDuplicate}
        isDuplicating={isDuplicating}
      />
      <ClientSettingEditButton
        onClick={onEdit}
        isDisabled={isDisabled || isGeneratingBudgets || isEditingDisabled}
      />
      <TriggerOptimizerRunForSettings
        clientSettings={[clientSetting]}
        isRunning={isGeneratingBudgets}
        hasError={hadGeneratingBudgetsError}
      />
    </div>
  ) : null;
}

function ClientSettingDeleteButton({
  onClick,
  isDisabled,
}: {
  onClick: () => void;
  isDisabled: boolean;
}) {
  return (
    <TertiaryButton
      onClick={onClick}
      className={twMerge(
        "uppercase text-mars [&_i]:text-mars hover:bg-mars-2",
        "flex items-center justify-center gap-2",
        clsx({
          "text-disabled [&_i]:text-disabled": isDisabled,
        }),
      )}
      disabled={isDisabled}
    >
      <i className="fa-regular fa-trash" />
      Delete
    </TertiaryButton>
  );
}

function ClientSettingDuplicateButton({
  onClick,
  isDuplicating,
}: {
  onClick: () => void;
  isDuplicating: boolean;
}) {
  return (
    <TertiaryButton
      onClick={onClick}
      className="uppercase flex items-center justify-center gap-2"
      loading={isDuplicating}
    >
      <i className="fa-regular fa-copy" />
      Duplicate
    </TertiaryButton>
  );
}

function ClientSettingEditButton({
  onClick,
  isDisabled,
}: {
  onClick: () => void;
  isDisabled: boolean;
}) {
  return (
    <SecondaryButton
      onClick={onClick}
      className="uppercase flex items-center justify-center gap-2"
      disabled={isDisabled}
    >
      <i className="fa-regular fa-pen" />
      Edit
    </SecondaryButton>
  );
}

function ClientSettingConfirmDeleteCard({
  settingName,
  onDelete,
  onCancel,
  isDestroying,
  errorText,
}: {
  settingName: string;
  onDelete: () => void;
  onCancel: () => void;
  isDestroying: boolean;
  errorText?: null | string;
}) {
  return (
    <div
      className={clsx(
        "w-full flex justify-between items-center mb-2",
        "rounded-md pl-4 pr-4 pt-[17px] pb-[17px]",
        "bg-mars-2",
      )}
    >
      <div className="flex flex-col justify-start items-start gap-2">
        {errorText ? (
          <span className="text-sm font-semibold text-danger">{errorText}</span>
        ) : (
          <>
            <span className="font-semibold text-sm">
              Delete &#34;{settingName}&#34;?
            </span>
            <span className="text-2xs">
              All associated budget mixes will also be deleted
            </span>
          </>
        )}
      </div>
      <div className="flex justify-end items-center gap-4">
        <SecondaryButton onClick={onCancel} className="uppercase">
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={onDelete}
          className="uppercase bg-mars hover:br-mars+1"
          loading={isDestroying}
          disabled={!!errorText}
        >
          Yes, Delete
        </PrimaryButton>
      </div>
    </div>
  );
}

export function TriggerOptimizerRunForSettingsConfirmationModal({
  loading,
  closeModal,
  submit,
  isOpen,
  adoptedMixWillBeDeleted,
}: {
  isOpen: boolean;
  closeModal: () => void;
  submit: () => void;
  loading: boolean;
  adoptedMixWillBeDeleted: boolean;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      titleContent={<div className="text-lg font-semibold">Are you sure?</div>}
    >
      <div className="flex flex-col h-full">
        <div>
          <div>You have existing Budget Mixes.</div>
          <div className="mt-1">
            Re-running will delete all existing budget recommendations for the
            associated setting(s), and create a new set of budget
            recommendations which may be different.{" "}
          </div>
          {adoptedMixWillBeDeleted && (
            <div className="text-mars+1">
              Your current adopted mix will be deleted as a part of this
              operation.
            </div>
          )}
        </div>
        <div className="flex justify-end self-end w-full">
          <TertiaryButton onClick={submit} disabled={loading} loading={loading}>
            RERUN
          </TertiaryButton>
          <TertiaryButton onClick={closeModal} disabled={loading}>
            CLOSE
          </TertiaryButton>
        </div>
      </div>
    </Modal>
  );
}

export function TriggerOptimizerRunForSettings({
  clientSettings,
  isRunning,
  hasError,
}: {
  clientSettings: ClientSetting[];
  isRunning: boolean;
  hasError: boolean;
}) {
  const toast = useToasts();
  const [showRerunConfirmationModal, setShowRerunConfirmationModal] =
    useState(false);

  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);

  const isDisabled = isRunning;

  const { selectedCycle } = useSelectedCycle();
  const { user } = useUser();
  const isAdminMode = useMMMAdminMode(user);
  const [budgetMixes, setBudgetMixes] = useBudgetMixes();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const clientSettingIds = new Set(clientSettings.map((cs) => cs.id));
  const budgetMixClientSettingIds = new Set(
    budgetMixes
      .filter((mix) => mix.is_blessed)
      .map((mix) => mix.client_setting_id),
  );
  const someClientSettingHasExistingBudgetMixes =
    clientSettingIds.intersection(budgetMixClientSettingIds).size > 0;

  const someClientSettingHasAdoptedMix = Boolean(
    adoptedBudgetMix &&
      clientSettingIds.has(adoptedBudgetMix.client_setting_id),
  );

  const [triggerOptimizationRun, { loading }] = useNorthbeamMutation<
    TriggerOptimizerRunForClientSettings,
    TriggerOptimizerRunForClientSettingsVariables
  >(TRIGGER_OPTIMIZER_RUN_FOR_CLIENT_SETTINGS, {
    awaitRefetchQueries: true,

    refetchQueries: [
      {
        query: BUDGET_MIXES_FOR_CYCLE_QUERY,
        variables: {
          id: selectedCycle.id,
          blessed: !isAdminMode,
        },
      },
    ],

    onCompleted(data) {
      toast.addToast(
        "Triggered Budget Recommendation Creating, please check back in 1 hour",
        { appearance: "success", autoDismiss: true },
      );
      setShowRerunConfirmationModal(false);
      setBudgetMixes((prevMixes) =>
        prevMixes.filter((mix) => !clientSettingIds.has(mix.client_setting_id)),
      );
    },
    onError(error) {
      toast.addToast(
        "Error triggering budget recommendations, please try again in 10 minutes",
        {
          appearance: "error",
          autoDismiss: true,
        },
      );
    },
  });
  const onSubmit = useCallback(() => {
    if (!isDisabled) {
      triggerOptimizationRun({
        variables: {
          clientSettingIds: clientSettings.map((s) => s.id),
        },
      });
    }
  }, [clientSettings, isDisabled, triggerOptimizationRun]);

  const getTooltipText = () => {
    if (isRunning) {
      return "Generating your budgets...";
    } else if (hasError) {
      return "Error generating your budget recommendations. Click to re-run.";
    } else if (isDisabled) {
      return "Unable to generate budget recommendations.";
    }

    return "Create budget recommendations.";
  };

  const onSubmitConfirmationModal = () => {
    trackMMMEvent({
      component:
        ExploreBudgetsTrackedComponent.GENERATE_BUDGETS_CONFIRMATION_MODAL,
      action: "onSubmit",
      actionInfo: "Regenerate budget mixes for setting",
    });
    onSubmit();
  };

  return (
    <>
      <TriggerOptimizerRunForSettingsConfirmationModal
        loading={loading}
        submit={onSubmitConfirmationModal}
        isOpen={showRerunConfirmationModal}
        closeModal={() => {
          trackMMMEvent({
            component:
              ExploreBudgetsTrackedComponent.GENERATE_BUDGETS_CONFIRMATION_MODAL,
            action: "onClose",
            actionInfo: "Declined to regenerate budget mixes for setting",
          });
          setShowRerunConfirmationModal(false);
        }}
        adoptedMixWillBeDeleted={someClientSettingHasAdoptedMix}
      />
      <GenericTooltip content={getTooltipText()} noInfoCircle placement="top">
        <div className=" px-2 py-1 flex flex-col items-center">
          <div className="h-6 mb-1.5">
            {loading ? (
              <Spinner />
            ) : (
              <i
                className={clsx(" fa-solid fa-play text-xl", {
                  "text-primary cursor-pointer": !isDisabled && !hasError,
                  "text-disabled cursor-not-allowed": isDisabled,
                  "text-mars cursor-pointer": hasError && !isDisabled,
                })}
                onClick={() => {
                  trackMMMEvent({
                    component:
                      clientSettings.length > 1
                        ? ExploreBudgetsTrackedComponent.GENERATE_BUDGETS_FOR_ALL_CLIENT_SETTINGS_BUTTON
                        : ExploreBudgetsTrackedComponent.GENERATE_BUDGETS_FOR_CLIENT_SETTING_BUTTON,
                    action: "onClick",
                    actionInfo: "Clicked to generate budget recommendations",
                  });
                  if (someClientSettingHasExistingBudgetMixes) {
                    setShowRerunConfirmationModal(true);
                  } else {
                    onSubmit();
                  }
                }}
              />
            )}
          </div>
          <div className="text-xs">Create budgets</div>
        </div>
      </GenericTooltip>
    </>
  );
}

export const TRIGGER_OPTIMIZER_RUN_FOR_CLIENT_SETTINGS = gql`
  mutation TriggerOptimizerRunForClientSettings($clientSettingIds: [String!]!) {
    triggerOptimizerRunForClientSettings(clientSettingIds: $clientSettingIds) {
      message
    }
  }
`;
